import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import {
  useProjectContactMutations,
  useProjectContactProjects,
  useProjectContacts,
} from "../hooks";
import type { ProjectContact, ProjectContactInput } from "../types";
import { apiErr } from "../utils";
import { PageHeader, TableShell, Th } from "../components/ui";

function ContactFormModal({ contact, onClose }: { contact: ProjectContact | null; onClose: () => void }) {
  const m = useProjectContactMutations();
  const [name, setName] = useState(contact?.name ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [position, setPosition] = useState(contact?.position ?? "");
  const [notes, setNotes] = useState(contact?.notes ?? "");
  const [isActive, setIsActive] = useState(contact?.is_active ?? true);
  const [error, setError] = useState<string | null>(null);

  const saving = m.create.isPending || m.update.isPending;

  const submit = async () => {
    setError(null);
    if (!name.trim()) {
      setError("Imię i nazwisko są wymagane.");
      return;
    }
    const payload: ProjectContactInput = {
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim(),
      position: position.trim(),
      notes: notes.trim(),
      is_active: isActive,
    };
    try {
      if (contact) await m.update.mutateAsync({ id: contact.id, payload });
      else await m.create.mutateAsync(payload);
      onClose();
    } catch (e) {
      setError(apiErr(e));
    }
  };

  return (
    <Modal open onClose={onClose} title={contact ? "Edytuj kontakt" : "Nowy kontakt"}>
      <div className="space-y-3">
        <label className="block">
          <span className="section-label">Imię i nazwisko *</span>
          <input className="input-base mt-1" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="section-label">Telefon</span>
            <input className="input-base mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </label>
          <label className="block">
            <span className="section-label">E-mail</span>
            <input className="input-base mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="section-label">Stanowisko / firma</span>
          <input
            className="input-base mt-1"
            placeholder="np. ENERGA-Operator, RD Gdańsk"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="section-label">Uwagi</span>
          <textarea className="input-base mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <span className="section-label">Aktywny</span>
        </label>

        {error && <p className="text-xs text-[var(--color-primary)] font-mono">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose}>Anuluj</Button>
          <Button size="sm" disabled={!name.trim()} loading={saving} onClick={submit}>
            {contact ? "Zapisz" : "Utwórz"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Expandable "w których projektach" — lazy-loaded when the row is opened.
function ContactProjects({ contactId }: { contactId: string }) {
  const { data, isLoading } = useProjectContactProjects(contactId);
  if (isLoading) return <div className="px-3 py-2"><Spinner /></div>;
  const projects = data?.projects ?? [];
  if (projects.length === 0) {
    return <p className="px-3 py-2 section-label normal-case tracking-normal">Brak powiązanych projektów.</p>;
  }
  return (
    <ul className="px-3 py-2 space-y-1">
      {projects.map((p) => (
        <li key={p.connection_id} className="text-xs flex items-center gap-2">
          {p.project_id ? (
            <Link to={`/power-projects/${p.project_id}`} className="text-[var(--color-primary)] hover:underline">
              {p.project_name}
            </Link>
          ) : (
            <span>{p.project_name}</span>
          )}
          <span className="font-mono text-[10px] text-[var(--color-text-muted)]">{p.roles.join(", ")}</span>
        </li>
      ))}
    </ul>
  );
}

export function ContactsPage() {
  const [search, setSearch] = useState("");
  const { data, isLoading } = useProjectContacts();
  const m = useProjectContactMutations();
  const [editing, setEditing] = useState<ProjectContact | null>(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = data ?? [];
    if (!q) return list;
    return list.filter((c) =>
      [c.name, c.phone, c.email, c.position].some((f) => f?.toLowerCase().includes(q)),
    );
  }, [data, search]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Książka telefoniczna"
        subtitle="Wspólna baza osób prowadzących sprawy w projektach ZE (kontakty z umowy i z ST/WP) · DLC_01"
        actions={<Button size="sm" onClick={() => setCreating(true)}>+ Nowy kontakt</Button>}
      />


      <div className="max-w-sm">
        <input
          className="input-base text-sm"
          placeholder="Szukaj po nazwisku, telefonie, e-mailu…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Imię i nazwisko</Th>
              <Th>Telefon</Th>
              <Th>E-mail</Th>
              <Th>Stanowisko / firma</Th>
              <Th className="text-right">Projekty</Th>
              <Th className="text-right">Akcje</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <Fragment key={c.id}>
                <tr
                  className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors"
                >
                  <td className="px-3 py-2.5 font-medium text-[var(--color-text-primary)]">
                    {c.name}
                    {!c.is_active && (
                      <span className="ml-2 text-[10px] font-mono text-[var(--color-text-muted)]">nieaktywny</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[13px]">
                    {c.phone ? <a href={`tel:${c.phone}`} className="hover:text-[var(--color-primary)]">{c.phone}</a> : "—"}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[13px]">
                    {c.email ? (
                      <a href={`mailto:${c.email}`} className="text-[var(--color-primary)] hover:underline">{c.email}</a>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-[var(--color-text-secondary)]">{c.position || "—"}</td>
                  <td className="px-3 py-2.5 text-right">
                    {c.project_count > 0 ? (
                      <button
                        className="font-mono text-xs text-[var(--color-primary)] hover:underline"
                        onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      >
                        {c.project_count} {expanded === c.id ? "▲" : "▼"}
                      </button>
                    ) : (
                      <span className="section-label">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>Edytuj</Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.confirm(`Usunąć kontakt „${c.name}"? Powiązania w projektach zostaną odpięte.`) && m.remove.mutate(c.id)}
                      >
                        Usuń
                      </Button>
                    </div>
                  </td>
                </tr>
                {expanded === c.id && (
                  <tr className="bg-[var(--color-bg-elevated)]">
                    <td colSpan={6}><ContactProjects contactId={c.id} /></td>
                  </tr>
                )}
              </Fragment>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center section-label tracking-wide">
                  {search ? "Brak kontaktów pasujących do wyszukiwania." : "Książka jest pusta. Dane pojawią się z metadanych projektów lub dodaj kontakt ręcznie."}
                </td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}

      {(creating || editing) && (
        <ContactFormModal contact={editing} onClose={() => { setCreating(false); setEditing(null); }} />
      )}
    </div>
  );
}
